// src/wiring/manifest/omnicastSurfaceWitness.public.ts
//
// Public tier / witness-safe capability.
// - Listens to OmniCast read-model events for health/monitoring
// - Does NOT import OmniCine internals
// - Does NOT create authority logic
//

import type { CapabilityManifest } from "@/contracts/capabilities/CapabilityManifest";
import { report } from "@/wiring/telemetry/report";

import {
  SYNK_OPS_METRIC_EVENT,
  SYNK_OPS_VIOLATION_EVENT,
  type SynkOpsMetricDetailV1,
  type SynkOpsViolationDetailV1,
} from "@/contracts/ops/OpsEvents";

// Import from your existing OmniCast read-model bus file.
import {
  OMNICAST_CONTEXT_EVENT,
  OMNICAST_NPC_RUNTIME_EVENT,
  OMNICAST_CLOCK_EVENT,
} from "@/components/OmniCast/bus/omnicastReadEvents";

const CAP_ID = "omnicast.surface.witness" as const;

function safeDispatch(name: string, detail: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // never throw from manifest
  }
}

function emitMetric(name: string, value: number, channel?: string): void {
  const d: SynkOpsMetricDetailV1 = {
    v: 1,
    at: Date.now(),
    kind: "custom",
    name,
    value,
    band: "normal",
    channel,
    capabilityId: CAP_ID,
  };
  safeDispatch(SYNK_OPS_METRIC_EVENT, d);
}

function emitViolation(where: string, reason: string, channel?: string): void {
  const d: SynkOpsViolationDetailV1 = {
    v: 1,
    at: Date.now(),
    severity: "warning",
    channel: channel ?? "unknown",
    where,
    reason,
    schema: "omnicast.surface.witness",
  };
  safeDispatch(SYNK_OPS_VIOLATION_EVENT, d);
}

export const OmniCastSurfaceWitnessPublicManifest: CapabilityManifest = {
  id: CAP_ID,
  version: "1.0.0",
  safety: "witness",
  tier: "public",
  provides: ["witness.surface", "telemetry.health"],

  install() {
    const w = window as any;

    // Idempotency guard
    w.__synk_capabilities = w.__synk_capabilities || {};
    if (w.__synk_capabilities.omnicastSurfaceWitnessInstalled) {
      report("capability.warn", { id: CAP_ID, reason: "already_installed" });
      return () => {};
    }
    w.__synk_capabilities.omnicastSurfaceWitnessInstalled = true;

    // Counters (bounded semantics: only counts)
    let ctxCount = 0;
    let npcCount = 0;
    let clockCount = 0;

    const onCtx = (_e: Event) => {
      try {
        ctxCount++;
        // Sample to keep overhead tiny
        if (ctxCount % 50 === 0) {
          emitMetric("omnicast.context.count", ctxCount, OMNICAST_CONTEXT_EVENT);
          report("capability.metric", {
            id: CAP_ID,
            metric: "omnicast.context.count",
            value: ctxCount,
          });
        }
      } catch {
        emitViolation("omnicast.surface.witness", "handler_failed", OMNICAST_CONTEXT_EVENT);
      }
    };

    const onNpc = (_e: Event) => {
      try {
        npcCount++;
        if (npcCount % 50 === 0) {
          emitMetric("omnicast.npc_runtime.count", npcCount, OMNICAST_NPC_RUNTIME_EVENT);
          report("capability.metric", {
            id: CAP_ID,
            metric: "omnicast.npc_runtime.count",
            value: npcCount,
          });
        }
      } catch {
        emitViolation("omnicast.surface.witness", "handler_failed", OMNICAST_NPC_RUNTIME_EVENT);
      }
    };

    const onClock = (_e: Event) => {
      try {
        clockCount++;
        if (clockCount % 200 === 0) {
          emitMetric("omnicast.clock.count", clockCount, OMNICAST_CLOCK_EVENT);
          report("capability.metric", {
            id: CAP_ID,
            metric: "omnicast.clock.count",
            value: clockCount,
          });
        }
      } catch {
        emitViolation("omnicast.surface.witness", "handler_failed", OMNICAST_CLOCK_EVENT);
      }
    };

    window.addEventListener(OMNICAST_CONTEXT_EVENT, onCtx as EventListener);
    window.addEventListener(OMNICAST_NPC_RUNTIME_EVENT, onNpc as EventListener);
    window.addEventListener(OMNICAST_CLOCK_EVENT, onClock as EventListener);

    report("capability.installed", { id: CAP_ID, tier: "public", safety: "witness" });

    // Optional: emit a one-time metric so Ops can see it immediately
    emitMetric("capability.installed", 1);

    return () => {
      try {
        window.removeEventListener(OMNICAST_CONTEXT_EVENT, onCtx as EventListener);
        window.removeEventListener(OMNICAST_NPC_RUNTIME_EVENT, onNpc as EventListener);
        window.removeEventListener(OMNICAST_CLOCK_EVENT, onClock as EventListener);

        if (w.__synk_capabilities) {
          delete w.__synk_capabilities.omnicastSurfaceWitnessInstalled;
        }

        report("capability.uninstalled", { id: CAP_ID });
      } catch {
        // never throw on uninstall
      }
    };
  },
};
